import React, { useState } from 'react';
import { Trans, withTranslation, WithTranslation } from 'react-i18next';
import styled from 'styled-components/macro';

import { H1, P } from '../../../../components/Typography';
import Modal from '../../../../components/Modal';
import { Button, ButtonGroup } from '../../../../components/Buttons';
import { distances, grid } from '../../../../styles/constants';
import { SwishCallbackModalState, State as SwishState } from '../reducer';
import { LoadingOverlay } from '../../../../components/Loading';
import { AccountIdsProps, withAccountIds } from '../../../../auth/accessToken/withAccountIds';
import { Formik, FormikValues } from 'formik';
import { validate, validatePositiveNumber, validateRequired } from '../../../../helpers/validation';
import { getValueAt } from '../../../../helpers/getValueAt';
import { Input, ValidationState } from '../../../../components/Forms';
import { SwishConnectionCallback } from '../../../../types/management-auth/generated';
import { ToggleDetails } from '../../connections/components/styledListItemComponents';

interface ValidatedInputProps extends WithTranslation {
    values: any;
    initialValues: any;
    errors: any;
    path: string;
    onChange: (e: React.FormEvent<HTMLInputElement>) => void;
    onBlur: (e: React.FormEvent<HTMLInputElement>) => void;
    touched: any;
    required?: boolean;
    maxlength?: number;
    type?: 'text' | 'password';
    formatter?: (value: string) => string;
    autoComplete?: string;
    autoFocus?: boolean;
}

const ValidatedInput = (
    {
        t,
        values,
        initialValues,
        errors,
        path,
        onChange,
        onBlur,
        touched,
        required,
        maxlength,
        type,
        autoComplete,
        autoFocus,
    }: ValidatedInputProps
) => {
    const value = getValueAt(path, values);
    const initialValue = getValueAt(path, initialValues);
    const error = getValueAt(path, errors);
    const was_touched = getValueAt(path, touched);
    const validation =
        (was_touched || initialValue) && error
            ? {
                state: ValidationState.Invalid,
                message: error,
            }
            : undefined;
    const label = required
        ? `${t(`settings.payment_connections.swish_callback_modal.fields.${path}`)} * `
        : t(`settings.payment_connections.swish_callback_modal.fields.${path}`);
    return (
        <InputWrapper>
            <Input
                label={label}
                placeholder={t(`settings.payment_connections.swish_callback_modal.placeholders.${path}`)}
                type={type || 'text'}
                name={path}
                value={value || ''}
                onChange={(e) => {
                    onChange(e);
                }}
                onBlur={(e) => {
                    onChange(e);
                    onBlur(e);
                }}
                validation={validation}
                maxlength={maxlength}
                autoComplete={autoComplete}
                autoFocus={autoFocus}
            />
        </InputWrapper>
    );
};

const InputWrapper = styled.div`
    margin-right: ${grid.gutter};

    &:last-child {
        margin: 0;
    }
`;

const TranslatedValidatedInput = withTranslation()(ValidatedInput);

export interface SwishCallbackModalProps extends WithTranslation, AccountIdsProps {
    closeSwishCallbackModal: () => void;
    completeSwishConnection: (accountId: string, userId: string, data: SwishConnectionCallback) => void;
    swish: SwishState;
    userId: string;
}

const SwishCallbackModal = (
    {
        t,
        swish,
        closeSwishCallbackModal,
        completeSwishConnection,
        prodAccountId,
        userId,
    }: SwishCallbackModalProps
) => {
    const [overrideSettings, setOverrideSettings] = useState(false);

    if (!prodAccountId) {
        return null;
    }

    if (swish.swishModalState === SwishCallbackModalState.Closed) {
        return null;
    }

    const initialFormValues = {
        swish_number: '',
        cert: '',
        passphrase: '',
        key: '',
        ca: '',
    };

    return (
        <Modal fixedWidth>
            {[SwishCallbackModalState.PreApplicationInfo, SwishCallbackModalState.InitiateCallbackLoading].includes(
                swish.swishModalState
            ) && (
                <>
                    <H1>{t('settings.payment_connections.swish_callback_modal.title_intro')}</H1>
                    <Formik
                        validateOnChange={true}
                        enableReinitialize
                        initialValues={initialFormValues as FormikValues}
                        validate={(values) => {
                            let errors: { [key: string]: string } = {};
                            if (!values) {
                                // this should not happen but...
                                return { error: 'account not loaded yet...' };
                            }

                            const validators = [
                                {
                                    path: 'swish_number',
                                    validator: validateRequired(
                                        t(
                                            'settings.payment_connections.swish_callback_modal.invalid.swish_number.required'
                                        )
                                    ),
                                },
                                {
                                    path: 'swish_number',
                                    validator: validatePositiveNumber(
                                        t(
                                            'settings.payment_connections.swish_callback_modal.invalid.passphrase.invalid'
                                        )
                                    ),
                                },
                            ];

                            errors = validators.reduce((acc, elem) => {
                                return validate(elem.path, elem.validator, values, acc);
                            }, errors);

                            return errors;
                        }}
                        onSubmit={(values) => {
                            const payload: SwishConnectionCallback = {
                                swish_number: values.swish_number.trim(),
                                cert: values.cert?.trim(),
                                passphrase: values.passphrase?.trim(),
                                key: values.key?.trim(),
                                ca: values.ca?.trim(),
                                payment_options: [
                                    {
                                        currencies: ['SEK'],
                                        type: 'swish.swish',
                                    },
                                ],
                            };
                            completeSwishConnection(prodAccountId as string, userId, payload);
                        }}
                    >
                        {({ values, errors, handleChange, handleBlur, touched, handleSubmit }) => (
                            <form onSubmit={handleSubmit}>
                                <SpacedWrapper>
                                    <P>
                                        <Trans i18nKey="settings.payment_connections.swish_callback_modal.intro" />
                                    </P>
                                    <SpacedWrapper>
                                        <TranslatedValidatedInput
                                            path="swish_number"
                                            initialValues={initialFormValues}
                                            values={values}
                                            errors={errors}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            touched={touched}
                                            required
                                            autoFocus
                                        />
                                        <InputWrapper>
                                            <ToggleDetails onClick={() => setOverrideSettings(!overrideSettings)}>
                                                {t('settings.payment_connections.swish_callback_modal.advanced_toggle', {
                                                    context: overrideSettings ? 'hide' : 'show',
                                                })}
                                            </ToggleDetails>
                                        </InputWrapper>
                                        {overrideSettings && (
                                            <React.Fragment>
                                                <TranslatedValidatedInput
                                                    path="cert"
                                                    initialValues={initialFormValues}
                                                    values={values}
                                                    errors={errors}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    touched={touched}
                                                    autoComplete="nope"
                                                />
                                                <TranslatedValidatedInput
                                                    path="passphrase"
                                                    initialValues={initialFormValues}
                                                    values={values}
                                                    errors={errors}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    touched={touched}
                                                    type="text"
                                                />
                                                <TranslatedValidatedInput
                                                    path="key"
                                                    initialValues={initialFormValues}
                                                    values={values}
                                                    errors={errors}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    touched={touched}
                                                />
                                                <TranslatedValidatedInput
                                                    path="ca"
                                                    initialValues={initialFormValues}
                                                    values={values}
                                                    errors={errors}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    touched={touched}
                                                />
                                            </React.Fragment>
                                        )}
                                    </SpacedWrapper>
                                </SpacedWrapper>
                                <ButtonGroup>
                                    <Button
                                        type="submit"
                                        disabled={
                                            Object.keys(errors || {}).length > 0 ||
                                            Object.keys(touched || {}).length === 0
                                        }
                                    >
                                        {t(
                                            'settings.payment_connections.swish_callback_modal.send_callback_call_to_action'
                                        )}
                                    </Button>
                                    <Button className="alt" onClick={closeSwishCallbackModal}>
                                        {t('settings.payment_connections.swish_callback_modal.cancel')}
                                    </Button>
                                </ButtonGroup>
                            </form>
                        )}
                    </Formik>
                    {swish.swishModalState === SwishCallbackModalState.InitiateCallbackLoading && <LoadingOverlay />}
                </>
            )}
            {swish.swishModalState === SwishCallbackModalState.CallbackError && (
                <>
                    <H1>{t('settings.payment_connections.swish_callback_modal.title_api_error')}</H1>e
                    <SpacedWrapper>
                        <P>
                            <Trans i18nKey="settings.payment_connections.swish_callback_modal.api_error" />
                        </P>
                    </SpacedWrapper>
                    <ButtonGroup>
                        <Button onClick={closeSwishCallbackModal}>
                            {t('settings.payment_connections.swish_callback_modal.close')}
                        </Button>
                    </ButtonGroup>
                </>
            )}
            {swish.swishModalState === SwishCallbackModalState.CallbackSuccess && (
                <>
                    <H1>{t('settings.payment_connections.swish_callback_modal.title_success')}</H1>

                    <SpacedWrapper>
                        <P>
                            <Trans i18nKey="settings.payment_connections.swish_callback_modal.success" />
                        </P>
                    </SpacedWrapper>

                    <ButtonGroup>
                        <Button onClick={closeSwishCallbackModal}>
                            {t('settings.payment_connections.swish_callback_modal.close')}
                        </Button>
                    </ButtonGroup>
                </>
            )}
        </Modal>
    );
};

export default withAccountIds(withTranslation()(SwishCallbackModal));

const SpacedWrapper = styled.div`
    margin-top: ${distances.small};
    margin-bottom: ${distances.normal};
`;
